import React, { useEffect, useState } from 'react';
import LoginForm from '../components/auth/LoginForm';
import EmailCodeLoginForm from '../components/auth/EmailCodeLoginForm';
import SignupCodeForm from '../components/auth/SignupCodeForm';
import ForgotPasswordForm from '../components/auth/ForgotPassword';
import { useAuthStore } from '../store/authStore';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { trackSignupPageView } from '../utils/googleAnalytics';
import InstallPrompt from '../components/common/InstallPrompt';
import { pendingPreview } from '../utils/businessPreview';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const legacySignup = searchParams.get('mode') === 'signup';
  // Carried between sign-in and signup (and from the "no account yet" emails)
  // so nobody retypes it. Only something shaped like an address is used.
  const emailParam = (searchParams.get('email') || pendingPreview()?.email || '').trim();
  const initialEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam) ? emailParam.slice(0, 100) : '';
  const [activeTab, setActiveTab] = useState(
    () => location.pathname === '/auth/signup' || legacySignup ? 'signup-code' : 'email-code'
  );
  const [pickerError, setPickerError] = useState(null);
  const [picking, setPicking] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const memberships = useAuthStore((state) => state.memberships);
  const requiresTenantSelection = useAuthStore((state) => state.requiresTenantSelection);
  const switchTenant = useAuthStore((state) => state.switchTenant);

  useEffect(() => {
    if (location.pathname === '/auth/login' && legacySignup) {
      navigate('/auth/signup', { replace: true });
      return;
    }
    if (location.pathname === '/auth/signup') {
      setActiveTab('signup-code');
    } else if (location.pathname === '/auth/login') {
      setActiveTab((current) => current === 'signup-code' ? 'email-code' : current);
    }
  }, [legacySignup, location.pathname, navigate]);

  useEffect(() => {
    if (activeTab === 'signup-code') trackSignupPageView();
    document.title = activeTab === 'signup-code' ? 'Create your AI agent · Serviti AI' : 'Sign in · Serviti AI';
  }, [activeTab]);

  const goToWorkspace = (u) => {
    if (u?.tenant_id) {
      if (pendingPreview()) { navigate('/keep-agent', { replace: true }); return; }
      // The tenant layout checks the server-owned onboarding state. Completed
      // workspaces continue to the dashboard; unfinished ones stay here.
      navigate(`/t/${u.tenant_id}/get-started`, { replace: true });
    } else if (u?.is_superuser || u?.role === 'superadmin') {
      navigate('/platform/dashboard');
    } else {
      navigate('/welcome');
    }
  };

  const handleLoginSuccess = () => {
    const state = useAuthStore.getState();
    // If the backend told us this user has 2+ workspaces, stay on this
    // page and let the picker render below.
    if (state.requiresTenantSelection) {
      return;
    }
    goToWorkspace(state.user);
  };

  const handleSignupSuccess = (result) => {
    const signupUser = result?.user || useAuthStore.getState().user;
    if (signupUser?.tenant_id) {
      if (pendingPreview()) { navigate('/keep-agent', { replace: true }); return; }
      navigate(`/t/${signupUser.tenant_id}/get-started`, { replace: true });
      return;
    }
    goToWorkspace(signupUser);
  };

  const handlePickTenant = async (tenantId) => {
    setPicking(true);
    setPickerError(null);
    try {
      const data = await switchTenant(tenantId);
      goToWorkspace(data.user);
    } catch (err) {
      setPickerError(err?.response?.data?.detail || 'Could not enter workspace');
    } finally {
      setPicking(false);
    }
  };

  const showPicker = requiresTenantSelection && Array.isArray(memberships) && memberships.length > 1;

  const withEmail = (path, email) => (typeof email === 'string' && email ? `${path}?email=${encodeURIComponent(email)}` : path);

  const openSignup = (email) => {
    setActiveTab('signup-code');
    navigate(withEmail('/auth/signup', email));
  };

  const openSignIn = (email) => {
    setActiveTab('email-code');
    navigate(withEmail('/auth/login', email));
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div data-testid="auth-layout" className="mx-auto grid min-h-[calc(100vh-2rem)] max-w-md items-center gap-6 sm:min-h-[calc(100vh-4rem)]">
        <main className="w-full">
          <div className="mb-6 flex justify-center">
            <img src="/serviti_logo_dark.png" alt="Serviti AI" className="h-auto w-48 max-w-full object-contain" />
          </div>
          <InstallPrompt className="mb-4 rounded-2xl" />
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-8">
          {showPicker ? (
            <div className="space-y-3">
              <div className="mb-5">
                <h2 className="text-2xl font-bold tracking-tight text-gray-950">Choose a workspace</h2>
                {user?.username && (
                  <p className="mt-2 text-sm text-gray-600">Signed in as <strong>{user.username}</strong></p>
                )}
              </div>
              {pickerError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {pickerError}
                </div>
              )}
              {memberships.map((m) => (
                <button
                  key={m.tenant_id}
                  onClick={() => handlePickTenant(m.tenant_id)}
                  disabled={picking}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900">{m.tenant_name}</div>
                    <div className="text-xs text-gray-500">{m.tenant_role}</div>
                  </div>
                  <span className="text-gray-400">&rsaquo;</span>
                </button>
              ))}
              <button
                onClick={() => useAuthStore.getState().logout()}
                className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2"
              >
                Sign out
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'login' && (
                <LoginForm
                  onLoginSuccess={handleLoginSuccess}
                  onForgotPassword={() => setActiveTab('forgot')}
                  onUseEmailCode={() => setActiveTab('email-code')}
                  onCreateAccount={openSignup}
                />
              )}
              {activeTab === 'email-code' && (
                <EmailCodeLoginForm
                  key={`signin-${initialEmail}`}
                  onLoginSuccess={handleLoginSuccess}
                  onCreateAccount={openSignup}
                  initialEmail={initialEmail}
                />
              )}
              {activeTab === 'signup-code' && (
                <SignupCodeForm
                  key={`signup-${initialEmail}`}
                  onSignupSuccess={handleSignupSuccess}
                  onBack={openSignIn}
                  initialEmail={initialEmail}
                />
              )}
              {activeTab === 'forgot' && (
                <ForgotPasswordForm onBack={() => setActiveTab('login')} />
              )}
            </>
          )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthPage;
